import { Accordion, AccordionDetails, AccordionSummary, Avatar, Button, ButtonBase, Chip, Collapse, Divider, IconButton, InputAdornment, LinearProgress, TextField, Typography, darken, lighten } from '@mui/material';
import useCourseTheme from '../../../core/hooks/useCourseTheme';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import { useCourseCore } from '../../../core/modules/course';
import { Link } from 'react-router-dom';
import { INICIO, MECOURSES } from '../../../core/router/routes';
import useNewNavigation from '../../../core/hooks/useNewNavigation';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import { Module } from '../../../core/interfaces/module.interface';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { Category } from '../../../core/modules/course/interfaces/get-categories';
import SearchIcon from '@mui/icons-material/Search';

type StatusFilter = 'all' | 'pending' | 'completed';

const MIN_CATEGORIES_FOR_FILTERS = 2;

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'Todas' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'completed', label: 'Completadas' },
];

interface Props {
  modules: Module[];
  selectedClassId: string;
  onChanging: (arg0: string, arg1: string) => void;
  toggleRating: () => void;
  // En el celular el índice se abre a pantalla completa con su propio encabezado y botón de cerrar
  onClose?: () => void;
}

const StepsModal = (props: Props) => {
  const courseTheme = useCourseTheme();
  const { modules, selectedClassId, onChanging, toggleRating, onClose } = props;
  const isFullScreen = Boolean(onClose);
  const { enabled: newNavigation } = useNewNavigation();
  const { course, viewed, expandedModules, toggleExpandedModule } = useCourseCore();
  const selectedClassRef = useRef<HTMLDivElement | null>(null);

  const [categories, setCategories] = useState<{ category: Category; selected: boolean }[]>([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // "Elige tu ruta" parte cerrado: abierto ocupaba media pantalla y empujaba los
  // módulos del curso hacia abajo (los alumnos no los encontraban).
  const [filtersOpen, setFiltersOpen] = useState(false);

  const isCompleted = useMemo(() => {
    return !modules.some((module) => !module.classes.find((clazz) => viewed.includes(clazz.class.id)));
  }, [modules, viewed]);

  const selectedCategories = useMemo(() => categories.filter((cat) => cat.selected), [categories]);

  const isFiltering = useMemo(() => {
    return selectedCategories.length > 0 || Boolean(searchTerm.trim()) || statusFilter !== 'all';
  }, [selectedCategories, searchTerm, statusFilter]);

  const showRouteFilters = categories.length >= MIN_CATEGORIES_FOR_FILTERS;

  const activeFilterCount =
    selectedCategories.length + (searchTerm.trim() ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0);

  useEffect(() => {
    setCategories(modules.reduce((prev: { category: Category; selected: boolean }[], module) => {
      const categories = module.classes.flatMap(
        (mClazz) => mClazz.class.categories ?? [],
      );

      categories.forEach((cat: Category) => {
        if (!cat?.id) {
          return;
        }
        if (!prev.find((ct: { category: Category; selected: boolean }) => ct.category.id === cat.id)) {
          prev.push({ category: cat, selected: false });
        }
      });

      return prev;
    }, []));
  }, [modules]);

  // Desplazar la lista hasta la clase actual cuando su módulo está abierto. El módulo lo
  // abre MeCourse solo al llegar a una clase concreta (link, notificación o "Siguiente
  // clase"); al entrar al curso sin clase elegida no se abre ninguno.
  const selectedModuleId = useMemo(
    () => modules.find((module) => module.classes.some((mClazz) => mClazz.class.id === selectedClassId))?.id,
    [modules, selectedClassId],
  );
  const selectedModuleOpen = Boolean(selectedModuleId && expandedModules.includes(selectedModuleId));

  useEffect(() => {
    if (!selectedClassId || !selectedModuleOpen) {
      return;
    }
    // Esperar a que termine la animación del acordeón antes de desplazar.
    const timeout = setTimeout(() => {
      selectedClassRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 350);
    return () => clearTimeout(timeout);
  }, [selectedClassId, selectedModuleOpen]);

  const toggleCategory = useCallback((id: string) => {
    setCategories((prev) =>
      prev.map((cat) => ({
        ...cat,
        selected: cat.category.id === id ? !cat.selected : cat.selected,
      }))
    );
  }, []);

  const resetCategoryFilters = useCallback(() => {
    setCategories((prev) =>
      prev.map((cat) => ({
        ...cat,
        selected: false,
      }))
    );
  }, []);

  const handleClearFilters = useCallback(() => {
    resetCategoryFilters();
    setSearchTerm('');
    setStatusFilter('all');
  }, [resetCategoryFilters]);

  const modulesFiltered = useMemo(() => {
    if (!isFiltering) {
      return modules;
    }

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return modules.flatMap((module) => {
      const filteredClasses = module.classes.filter((moduleClass) => {
        const matchesCategory =
          !selectedCategories.length ||
          (moduleClass.class.categories ?? []).some((classCategory) =>
            selectedCategories.some((cat) => cat.category.id === classCategory.id)
          );
        if (!matchesCategory) {
          return false;
        }

        const matchesSearch =
          !normalizedSearchTerm || moduleClass.class.name.toLowerCase().includes(normalizedSearchTerm);
        if (!matchesSearch) {
          return false;
        }

        const isViewed = viewed.includes(moduleClass.class.id);
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'completed' && isViewed) ||
          (statusFilter === 'pending' && !isViewed);

        return matchesStatus;
      });

      if (!filteredClasses.length) {
        return [];
      }

      return {
        ...module,
        classes: filteredClasses,
      };
    });
  }, [isFiltering, modules, searchTerm, selectedCategories, statusFilter, viewed]);

  const showEmptyState = isFiltering && modulesFiltered.length === 0;

  const filteredClassCount = useMemo(
    () => modulesFiltered.reduce((count, module) => count + module.classes.length, 0),
    [modulesFiltered]
  );

  const { totalClasses, completedClasses, pendingClasses, progressPercent } = useMemo(() => {
    const total = modules.reduce((count, module) => count + module.classes.length, 0);
    const completed = modules.reduce(
      (count, module) =>
        count + module.classes.filter((moduleClass) => viewed.includes(moduleClass.class.id)).length,
      0
    );

    return {
      totalClasses: total,
      completedClasses: completed,
      pendingClasses: Math.max(total - completed, 0),
      progressPercent: total ? Math.round((completed / total) * 100) : 0,
    };
  }, [modules, viewed]);

  return (
    <div
      className="h-full overflow-y-auto"
      style={{
        background: courseTheme
          ? `radial-gradient(circle at top left, ${courseTheme.tintBorder}66, transparent 30%), linear-gradient(180deg,#f8fafc 0%,${courseTheme.tint} 100%)`
          : 'radial-gradient(circle at top left, rgba(124,58,237,0.16), transparent 30%), linear-gradient(180deg,#f8fafc 0%,#eef2ff 100%)',
      }}
    >
      <div className="flex flex-col gap-16 p-16">
        {isFullScreen ? (
          <div
            className="flex items-center justify-between gap-12 -mx-16 -mt-16 px-16 py-14 sticky top-0 z-10"
            style={{ background: '#fff', borderBottom: '1px solid #E2E8F0' }}
          >
            <div className="min-w-0">
              <Typography className="text-17 font-extrabold text-gray-900">Contenido del curso</Typography>
              <Typography className="text-12 text-gray-500 truncate">
                {course?.name} · {completedClasses} de {totalClasses} clases · {progressPercent}%
              </Typography>
            </div>
            <IconButton onClick={onClose} aria-label="Cerrar contenido del curso">
              <CloseIcon />
            </IconButton>
          </div>
        ) : (
          <>
          <Button
            to={newNavigation ? INICIO : MECOURSES}
            component={Link}
            color="secondary"
            variant="text"
            className="justify-start rounded-full"
            startIcon={<ArrowBackOutlinedIcon />}
            sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
          >
            {newNavigation ? 'Volver al inicio' : 'Volver a mis cursos'}
          </Button>

          <section
            className="rounded-3xl text-white p-16 shadow-lg overflow-hidden relative"
            style={{
              background: courseTheme?.gradient ?? 'linear-gradient(135deg, #1e1b4b 0%, #6d28d9 56%, #0ea5e9 100%)',
              boxShadow: courseTheme ? `0 24px 60px -36px ${courseTheme.dark}` : '0 24px 60px -36px rgba(30,27,75,0.9)',
            }}
          >
            <div
              className="absolute inset-0 opacity-30"
              style={{
                background:
                  'radial-gradient(circle at 78% 12%, rgba(255,255,255,0.5), transparent 22%), radial-gradient(circle at 8% 92%, rgba(255,255,255,0.26), transparent 26%)',
              }}
            />
            <div className="relative flex items-center gap-12">
              {course?.avatar ? (
                <Avatar
                  alt="course avatar"
                  src={course.avatar}
                  sx={{ width: 60, height: 60, border: '3px solid rgba(255,255,255,0.28)' }}
                />
              ) : (
                <Avatar sx={{ width: 60, height: 60, border: '3px solid rgba(255,255,255,0.28)', fontWeight: 800 }}>
                  {course?.name?.[0] ?? 'C'}
                </Avatar>
              )}
              <div className="flex-1">
                <Typography className="text-15 font-semibold text-white leading-snug break-words">
                  {course?.name}
                </Typography>
                <div className="mt-6 flex justify-end">
                  <Chip
                    label={isCompleted ? 'Completado' : 'En progreso'}
                    icon={<VerifiedIcon fontSize="small" />}
                    size="small"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.35)',
                      color: '#fff',
                      backgroundColor: isCompleted ? '#0F9D58' : 'rgba(255,193,7,0.85)',
                      '& .MuiChip-icon': { color: '#fff' },
                    }}
                    variant="outlined"
                  />
                </div>
              </div>
            </div>
            <div className="relative mt-16">
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    backgroundColor: '#fff',
                  },
                }}
              />
              <div className="flex justify-between text-12 text-white/80 mt-8">
                <span>{completedClasses} de {totalClasses} clases completadas</span>
                <span>{progressPercent}%</span>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-3 gap-8">
            {[
              { label: 'Módulos', value: modules.length },
              { label: 'Completadas', value: completedClasses },
              { label: 'Pendientes', value: pendingClasses },
            ].map((metric) => (
              <div
                key={metric.label}
                className="bg-white rounded-2xl border border-gray-100 py-12 text-center shadow-sm"
                style={{ boxShadow: '0 14px 34px -30px rgba(15,23,42,0.55)' }}
              >
                <Typography className="text-11 uppercase text-gray-500">{metric.label}</Typography>
                <Typography className="text-18 font-semibold text-gray-900 mt-4">{metric.value}</Typography>
              </div>
            ))}
          </section>
          </>
        )}

        {/* Con menos de 2 categorías no hay ruta que elegir: se oculta para no tapar los módulos. */}
        {showRouteFilters ? (
          <section
            className="bg-white rounded-3xl border border-gray-100 shadow-sm p-16 space-y-12"
            style={{ boxShadow: '0 18px 46px -34px rgba(15,23,42,0.55)' }}
          >
            <ButtonBase
              onClick={() => setFiltersOpen((prev) => !prev)}
              aria-expanded={filtersOpen}
              className="w-full flex items-center justify-between gap-8 text-left rounded-2xl"
            >
              <div className="flex items-center gap-8">
                <TuneIcon fontSize="small" sx={{ color: courseTheme?.main ?? '#7c3aed' }} />
                <div>
                  <Typography className="text-14 font-semibold text-gray-900">
                    Elige tu ruta
                  </Typography>
                  <Typography className="text-12 text-gray-500">
                    {activeFilterCount
                      ? `${activeFilterCount} ${activeFilterCount === 1 ? 'filtro activo' : 'filtros activos'} · ${filteredClassCount} clases`
                      : 'Filtra por categoría, estado o nombre'}
                  </Typography>
                </div>
              </div>
              <ExpandMoreIcon
                sx={{
                  color: '#64748b',
                  transition: 'transform 0.2s',
                  transform: filtersOpen ? 'rotate(180deg)' : 'none',
                }}
              />
            </ButtonBase>

            {!filtersOpen && isFiltering ? (
              <Chip
                className="text-11 cursor-pointer"
                label="Limpiar filtros"
                size="small"
                onDelete={handleClearFilters}
              />
            ) : null}

            <Collapse in={filtersOpen} timeout="auto" unmountOnExit={false}>
              <div className="space-y-12 pt-4">
                <div className="flex flex-wrap gap-8">
                  {categories.length ? (
                    (showAllCategories ? categories : categories.slice(0, 6)).map((cat) => (
                      <Chip
                        key={cat.category.id}
                        className="font-semibold text-11 cursor-pointer"
                        label={cat.category.name}
                        sx={{
                          color: () => (cat.selected ? darken(cat.category.color, 0.45) : darken('#c2c2c2', 0.4)),
                          backgroundColor: () => (cat.selected ? lighten(cat.category.color, 0.8) : lighten('#c2c2c2', 0.9)),
                        }}
                        size="small"
                        onClick={() => toggleCategory(cat.category.id)}
                      />
                    ))
                  ) : (
                    <Typography className="text-12 text-gray-500">Sin categorías disponibles.</Typography>
                  )}
                </div>
                {categories.length > 6 ? (
                  <Button
                    variant="text"
                    size="small"
                    className="justify-start px-0"
                    onClick={() => setShowAllCategories((prev) => !prev)}
                  >
                    {showAllCategories ? 'Ver menos categorías' : `Ver ${categories.length - 6} categorías más`}
                  </Button>
                ) : null}

                <TextField
                  value={searchTerm}
                  placeholder="Buscar clase..."
                  onChange={(event) => setSearchTerm(event.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <div className="flex flex-wrap items-center gap-8">
                  <Typography className="text-11 font-semibold text-gray-500">Estado:</Typography>
                  {STATUS_FILTERS.map((option) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      color={statusFilter === option.value ? 'secondary' : 'default'}
                      variant={statusFilter === option.value ? 'filled' : 'outlined'}
                      size="small"
                      onClick={() => setStatusFilter(option.value)}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between text-12 text-gray-500">
                  <span>
                    {isFiltering
                      ? `Mostrando ${filteredClassCount} clases`
                      : `Mostrando ${totalClasses} clases`}
                  </span>
                  {isFiltering ? (
                    <Chip
                      className="text-11 cursor-pointer"
                      label="Limpiar filtros"
                      size="small"
                      onDelete={handleClearFilters}
                    />
                  ) : null}
                </div>
              </div>
            </Collapse>
          </section>
        ) : null}

        <section
          className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden"
          style={{ boxShadow: '0 18px 46px -34px rgba(15,23,42,0.55)' }}
        >
          <div className="px-16 py-14 flex items-center justify-between gap-8">
            <Typography className="text-14 font-semibold text-gray-900">Contenido del curso</Typography>
            <Chip
              label={`${completedClasses}/${totalClasses}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          </div>
          <Divider />
          {showEmptyState ? (
            <Typography className="px-16 py-24 text-center text-gray-500 text-sm">
              No encontramos clases que coincidan con tus filtros.
            </Typography>
          ) : (
            <div className="divide-y">
              {modulesFiltered.map((module, mIdx) => {
                const moduleCompleted = module.classes.reduce(
                  (prev, curr) => (viewed.includes(curr.class.id) ? prev + 1 : prev),
                  0
                );

                return (
                  <Accordion
                    key={module.id}
                    expanded={expandedModules.includes(module.id)}
                    onChange={() => toggleExpandedModule(module.id)}
                    disableGutters
                    square={false}
                    sx={{
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                      backgroundColor: 'transparent',
                    }}
                  >
                    <AccordionSummary
                      className={clsx('px-16 py-12 border-b border-gray-100')}
                      sx={{
                        background:
                          expandedModules.includes(module.id)
                            ? courseTheme
                              ? `linear-gradient(90deg, ${courseTheme.tint}, #ffffff)`
                              : 'linear-gradient(90deg, rgba(124,58,237,0.1), rgba(14,165,233,0.06))'
                            : '#f8fafc',
                      }}
                    >
                      <div className="flex w-full items-center gap-8">
                        <CreateNewFolderOutlinedIcon className="text-secondary" fontSize="small" />
                        <div className="flex justify-between items-center w-full gap-8">
                          <div>
                            <Typography className="text-11 uppercase text-gray-500">
                              Módulo {mIdx + 1}
                            </Typography>
                            <Typography className="text-13 text-gray-900 font-medium">
                              {module.name}
                            </Typography>
                          </div>
                          <Chip
                            label={`${moduleCompleted}/${module.classes.length}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 700, backgroundColor: '#fff' }}
                          />
                        </div>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails className="p-0">
                      <div className="divide-y">
                        {module.classes.map((step, cIdx) => (
                          <div
                            key={`${mIdx} ${cIdx}`}
                            ref={step.class.id === selectedClassId ? selectedClassRef : undefined}
                            className={clsx(
                              'flex items-center justify-between cursor-pointer px-14 py-14 text-sm transition-all',
                              {
                                'border-l-4 shadow-inner': step.class.id === selectedClassId,
                                'bg-indigo-50 border-indigo-500': step.class.id === selectedClassId && !courseTheme,
                                'hover:bg-gray-50': step.class.id !== selectedClassId,
                              }
                            )}
                            // La clase elegida se marca con el color del curso
                            style={step.class.id === selectedClassId && courseTheme ? { backgroundColor: courseTheme.tint, borderLeftColor: courseTheme.main } : undefined}
                            onClick={() => onChanging(module.id, step.class.id)}
                          >
                            <div className="flex items-center">
                              {viewed.includes(step.class.id) ? (
                                <CheckCircleOutlineIcon color="success" fontSize="small" />
                              ) : (
                                <PanoramaFishEyeIcon className="text-gray-400" fontSize="small" />
                              )}
                              <span
                                className={clsx('ml-8 leading-snug', step.class.id === selectedClassId ? 'font-semibold' : 'text-gray-800', step.class.id === selectedClassId && !courseTheme && 'text-indigo-950')}
                                style={step.class.id === selectedClassId && courseTheme ? { color: courseTheme.dark } : undefined}
                              >
                                {step.class.name}
                              </span>
                            </div>
                            {viewed.includes(step.class.id) ? (
                              <Chip label="Visto" color="success" size="small" />
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 space-y-8">
          <div>
            <Typography className="text-14 font-semibold text-gray-900">
              Encuesta de satisfacción
            </Typography>
            <Typography className="text-12 text-gray-500">
              Cuéntanos cómo podemos mejorar tu experiencia en este curso.
            </Typography>
          </div>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RateReviewOutlinedIcon />}
            onClick={toggleRating}
          >
            Abrir encuesta
          </Button>
        </section>
      </div>
    </div>
  );
};

export default StepsModal;
